import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Name, ZomentumRoutes } from "@/Contracts";
import {
	ClientContactFormValues,
	AddClientContactRequest,
	ClientUserContactType
} from "@/Contracts/Clients";
import { Row, Col } from "antd";
import { CustomField } from "@/Contracts/Settings";
import ZRenderCustomFieldFormItem from "@organisms/ZRenderCustomFieldFormItem";
import ClientsActionCreators from "@/Actions/Clients";
import useDispatch from "@/Components/Utils/useDispatch";
import { processCustomFieldFormValues } from "@/Utils";
import ZMessage from "@molecules/ZMessage";
import { IAddClientContactDrawerProps } from "./interface";
import ZDrawer from "@molecules/ZDrawer";
import Form from "antd/lib/form";
import ZForm from "@organisms/ZForm";
import {
	EWidgets,
	IFormFieldProps,
	IFormProps,
	IFormSectionProps
} from "@organisms/ZForm/interface";
import { getClientContactFormItem } from "@templates/ZDrawers/ZClients/ZClientContact/getClientContactFormItem";
import ZTypography, { FontWeights } from "@atoms/ZTypography";
import ZIcon, { Icons } from "@atoms/ZIcon";
import { GlobalState } from "@/Reducers/interface";

import { useRouteMatch } from "react-router-dom";

const { ZText } = ZTypography;

export const AddClientContactDrawer: React.FC<IAddClientContactDrawerProps> = ({
	visible,
	onSubmit,
	onClose,
	activeTabContactType,
	clientId
}) => {
	const [shouldSetAsPrimaryContact, setShouldSetAsPrimaryContact] =
		useState<boolean>(false);
	const [customFieldList, setCustomFieldList] = useState<CustomField[]>(
		new Array<CustomField>()
	);

	const { isLoading, customFieldListClientContact } =
		useSelector((state: GlobalState) => {
			return {
				isLoading: state.clients.isLoading,
				customFieldListClientContact:
					state.customFields.customFieldList.client_user
			};
		});

	const [form] = Form.useForm();
	const dispatch = useDispatch();
	let firstRender = true;

	useEffect(() => {
		if (visible) {
			if (firstRender) {
				getCustomFieldsList();
			}
			firstRender = false;
		}
	}, [visible]);

	const getCustomFieldsList = () => {
		setCustomFieldList(customFieldListClientContact);
	};

	const handleClose = () => {
		setCustomFieldList([]);
		form.resetFields();
		setShouldSetAsPrimaryContact(false);
		onClose && onClose();
	};

	const isSalesActivityPage = !!useRouteMatch(
		ZomentumRoutes.SalesActivityPage
	);

	const handleAddClientContact = async (
		addClientContactFormValues: ClientContactFormValues
	) => {
		dispatch(ClientsActionCreators.showClientsLoader());
		try {
			if (!clientId) {
				if (isSalesActivityPage) {
					ZMessage.error(
						"Please select a Client or Opportunity first"
					);
					return;
				}
				ZMessage.error("There is no active client");
				return;
			}
			let addClientContactRequest = new AddClientContactRequest();
			addClientContactRequest.client_company_id = clientId;
			addClientContactRequest.name = new Name();
			addClientContactRequest.name.first =
				addClientContactFormValues.first_name;
			addClientContactRequest.name.last =
				addClientContactFormValues.last_name;
			addClientContactRequest.email = !!addClientContactFormValues.email
				?.length
				? addClientContactFormValues.email
				: null;
			addClientContactRequest.phone = addClientContactFormValues.phone;
			addClientContactRequest.mobile = addClientContactFormValues.mobile;
			addClientContactRequest.contact_type =
				addClientContactFormValues.contact_type;
			addClientContactRequest.job_title =
				addClientContactFormValues.job_title;
			addClientContactRequest.department =
				addClientContactFormValues.department;
			addClientContactRequest.mandatory_custom_field_check_require = true;
			const customFieldFormValues =
				addClientContactFormValues.custom_field_form_values;
			if (customFieldFormValues && customFieldList?.length > 0) {
				addClientContactRequest = {
					...addClientContactRequest,
					custom_fields: processCustomFieldFormValues(
						customFieldFormValues,
						customFieldList
					)
				};
			}
			const clientUserResponseAction = await dispatch(
				ClientsActionCreators.addClientContact(addClientContactRequest)
			);

			onSubmit &&
				(await onSubmit(clientUserResponseAction.addClientContact));
			handleClose();
			ZMessage.success("Contact added successfully");
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(ClientsActionCreators.hideClientsLoader());
		}
	};

	const handleOnContactTypeUpdate = (
		newContactType: ClientUserContactType
	) => {
		setShouldSetAsPrimaryContact(
			activeTabContactType != null &&
				activeTabContactType ===
					ClientUserContactType.SecondaryContact &&
				newContactType === ClientUserContactType.PrimaryContact
		);
	};

	const getFormItem = (
		customField: CustomField,
		index: number
	): IFormFieldProps | IFormFieldProps[] => {
		if (customField.system_field_id === "client_user_contact_type") {
			const contactTypeField: IFormFieldProps[] = [
				{
					key: "contact_type",
					label: "Contact Type",
					hasFeedback: true,
					required: true,
					message: "Please choose the contact type",
					initialValue: activeTabContactType || "",
					widget: EWidgets.SELECT,
					widgetProps: {
						allowClear: true,
						placeholder: "Please choose the contact type",
						onChange: handleOnContactTypeUpdate,
						disabled:
							(activeTabContactType != null &&
								activeTabContactType ===
									ClientUserContactType.PrimaryContact) ||
							isLoading,
						selectOptions: [
							{
								name: "Primary",
								value: ClientUserContactType.PrimaryContact
							},
							{
								name: "Secondary",
								value: ClientUserContactType.SecondaryContact
							}
						]
					}
				}
			];
			if (shouldSetAsPrimaryContact) {
				contactTypeField.push({
					key: "warning",
					render: () => (
						<Row className="mt-n4 mb-6">
							<Col
								offset={5}
								span={19}
								className="bg-gold-1 inline-flex items-center p-2 rounded"
							>
								<ZIcon
									name={Icons.WARNINGCIRCLE}
									size="sm"
									colorVariant={8}
									className="ml-0 mr-2"
								/>
								<ZText colorVariant={8}>
									The existing{" "}
									<ZText weight={FontWeights.MEDIUM}>
										Primary Contact
									</ZText>{" "}
									will be changed to{" "}
									<ZText weight={FontWeights.MEDIUM}>
										Secondary
									</ZText>
								</ZText>
							</Col>
						</Row>
					)
				});
			}
			return contactTypeField;
		} else {
			const ClientContactFromItem = getClientContactFormItem(
				customField.system_field_id,
				undefined,
				customField.mandatory_field_details?.for_ui
			);
			const isMobileField =
				customField.system_field_id === "client_user_mobile";
			return isMobileField
				? ClientContactFromItem ?? []
				: ClientContactFromItem ?? {
						key: `custom_client_contact_form_item_${index}`,
						render: () => (
							<ZRenderCustomFieldFormItem
								form={form}
								customField={customField}
								isLoading={isLoading}
							/>
						)
				  };
		}
	};

	const sections: IFormSectionProps[] = [
		{
			isIndentFormItems: false,
			meta: {
				formItemLayout: [5, 19],
				fields: [
					{
						key: "first_name",
						label: "First Name",
						hasFeedback: true,
						required: true,
						message: "Please enter first name",
						widget: EWidgets.INPUT,
						rules: [
							{
								whitespace: true,
								message: "Please enter first name"
							}
						],
						widgetProps: {
							width: "full",
							placeholder: "Please enter first name",
							"data-testid": "first-name"
						}
					},
					{
						key: "last_name",
						label: "Last Name",
						hasFeedback: true,
						required: true,
						message: "Please enter last name",
						rules: [
							{
								whitespace: true,
								message: "Please enter last name"
							}
						],
						widget: EWidgets.INPUT,
						widgetProps: {
							width: "full",
							placeholder: "Please enter last name",
							"data-testid": "last-name"
						}
					},
					...customFieldList
						?.map((customField, index) =>
							getFormItem(customField, index)
						)
						?.flat()
				]
			}
		}
	];

	const formProps: IFormProps = {
		onFinish: handleAddClientContact,
		key: "AddClientContactForm",
		sections,
		loading: isLoading,
		submitButtonText: "Save",
		isStickFooter: true,
		form,
		isCancelAllowed: true,
		onCancel: handleClose,
		isRightFooter: true
	};

	return (
		<ZDrawer
			visible={visible}
			closable={true}
			destroyOnClose={true}
			onClose={handleClose}
			title="Add Contact"
			removeDefaultPadding={true}
		>
			<ZForm {...formProps} className="py-4" />
		</ZDrawer>
	);
};

export default AddClientContactDrawer;
